import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trainingReportApi } from '../../services/trainingReportApi';
import { buildTrainingReportReview } from './trainingReportReview';
import './TrainingReportReview.css';

const SECTIONS = [
  'Datos generales',
  'Participantes',
  'Asistencia',
  'Resultados',
  'Evidencias',
];

const stripAboa = (value) => {
  const text = String(value || '').trim();
  return /^ABOA/i.test(text) ? text.slice(4).replace(/^[\s-]+/, '') : text;
};

const createParticipantDraft = (report = {}) =>
  (report.participantes || []).map((participant) => ({
    id_reporte_participante: Number(participant.id_reporte_participante),
    nombre_completo: participant.nombre_completo || '',
    matricula_alumno: stripAboa(participant.matricula_alumno),
    genero: participant.genero || '',
  }));

const issueStatusForSection = (review, section) => {
  const blockers = review.blockers.filter((item) => item.section === section);
  const warnings = review.warnings.filter((item) => item.section === section);
  if (blockers.length > 0) return { type: 'error', label: 'Bloqueo', count: blockers.length };
  if (warnings.length > 0) return { type: 'warning', label: 'Revisar', count: warnings.length };
  return { type: 'success', label: 'Correcto', count: 0 };
};

export const TrainingReportReviewStep = ({
  idReporte,
  accessToken,
  report,
  setReport,
  onPrevious,
  onNext,
}) => {
  const [reviewReport, setReviewReport] = useState(report || {});
  const [participants, setParticipants] = useState(() => createParticipantDraft(report));
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const autoLoadKeyRef = useRef(null);
  const requestInFlightRef = useRef(false);

  const review = useMemo(
    () => buildTrainingReportReview(reviewReport || {}, summary, []),
    [reviewReport, summary],
  );

  useEffect(() => {
    setReviewReport(report || {});
    setParticipants(createParticipantDraft(report));
  }, [report]);

  const refreshReview = useCallback(async () => {
    if (!accessToken || requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setLoading(true);
    setStatus({ type: 'info', message: 'Actualizando revisión...' });

    try {
      const [latest, latestSummary] = await Promise.all([
        trainingReportApi.get(idReporte, accessToken),
        trainingReportApi.summary(idReporte, accessToken),
      ]);

      setReviewReport(latest || {});
      setParticipants(createParticipantDraft(latest));
      setSummary(latestSummary || {});
      setStatus(null);
    } catch (error) {
      console.error('No se pudo actualizar la revisión:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible actualizar la revisión del reporte.',
      });
    } finally {
      requestInFlightRef.current = false;
      setLoading(false);
    }
  }, [accessToken, idReporte]);

  useEffect(() => {
    const autoLoadKey = `${idReporte || ''}:${accessToken || ''}`;
    if (!accessToken || autoLoadKeyRef.current === autoLoadKey) return;

    autoLoadKeyRef.current = autoLoadKey;
    refreshReview();
  }, [accessToken, idReporte, refreshReview]);

  const updateParticipant = (participantId, field, value) => {
    setParticipants((current) =>
      current.map((participant) =>
        participant.id_reporte_participante === participantId
          ? { ...participant, [field]: value }
          : participant,
      ),
    );
    setStatus(null);
  };

  const saveParticipantCorrections = async () => {
    if (!accessToken || saving || loading || participants.length === 0) return;

    const normalized = participants.map((participant) => ({
      id_reporte_participante: participant.id_reporte_participante,
      nombre_completo: String(participant.nombre_completo || '').trim(),
      matricula_alumno: stripAboa(participant.matricula_alumno),
      genero: participant.genero || null,
    }));

    const invalid = normalized.find(
      (participant) => !participant.nombre_completo || !participant.matricula_alumno,
    );
    if (invalid) {
      setStatus({
        type: 'error',
        message: 'Todos los participantes deben tener nombre y expediente laboral.',
      });
      return;
    }

    setSaving(true);
    setStatus({ type: 'info', message: 'Guardando correcciones de participantes...' });
    try {
      const updated = await trainingReportApi.updateParticipants(
        idReporte,
        normalized,
        accessToken,
      );
      const latestSummary = await trainingReportApi.summary(idReporte, accessToken);
      setReviewReport(updated || {});
      setParticipants(createParticipantDraft(updated));
      setSummary(latestSummary || {});
      setReport(updated);
      setStatus({
        type: 'success',
        message: 'Correcciones guardadas. Los cambios aplican solo a este reporte.',
      });
    } catch (error) {
      console.error('No se pudieron guardar las correcciones:', error);
      setStatus({
        type: 'error',
        message: error?.message || 'No fue posible guardar las correcciones de participantes.',
      });
    } finally {
      setSaving(false);
    }
  };

  const statusClass =
    status?.type === 'error'
      ? 'admin-operation-status-error'
      : status?.type === 'success'
        ? 'admin-operation-status-success'
        : 'admin-operation-status-warning';

  return (
    <>
      <div className="admin-operation-panel-heading">
        <div>
          <p className="admin-operation-eyebrow">Paso 5 de 7</p>
          <h2>Revisión y edición</h2>
          <p>
            Revisa la información antes de generar la vista previa. En este paso puedes
            corregir nombres, expediente laboral y género sin modificar el registro
            original del alumno.
          </p>
        </div>
        <span className={`training-report-status training-report-status-${reviewReport?.estado || 'borrador'}`}>
          {reviewReport?.estado === 'generado' ? 'Generado' : 'Borrador'}
        </span>
      </div>

      <div className={`training-report-review-readiness ${review.ready ? 'training-report-review-ready' : 'training-report-review-blocked'}`}>
        <div>
          <span>Estado de revisión</span>
          <strong>
            {loading
              ? 'Verificando...'
              : review.ready
                ? 'Listo para vista previa'
                : 'Requiere correcciones'}
          </strong>
          <small>
            {loading
              ? 'Consultando datos actualizados del reporte.'
              : review.ready
                ? 'No se detectaron errores bloqueantes.'
                : `${review.blockers.length} bloqueo(s) deben resolverse antes de generar el PDF definitivo.`}
          </small>
        </div>
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={refreshReview}
          disabled={loading || saving}
        >
          {loading ? 'Verificando...' : 'Actualizar revisión'}
        </button>
      </div>

      {status && (
        <div className={`admin-operation-status ${statusClass}`} role="status">
          {status.message}
        </div>
      )}

      <section className="training-report-review-editor">
        <div className="training-report-review-editor-heading">
          <div>
            <span>Revisión editable de participantes</span>
            <strong>Nombre y expediente laboral</strong>
            <small>
              El prefijo ABOA se elimina del expediente mostrado en el reporte. Los cambios
              se guardan únicamente en la copia del participante de este reporte.
            </small>
          </div>
          <button
            className="admin-operation-button"
            type="button"
            onClick={saveParticipantCorrections}
            disabled={loading || saving || participants.length === 0}
          >
            {saving ? 'Guardando...' : 'Guardar correcciones'}
          </button>
        </div>

        <div className="admin-operation-table-wrapper training-report-review-editor-wrapper">
          <table className="admin-operation-table training-report-review-editor-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nombre completo</th>
                <th>Expediente laboral</th>
                <th>Género</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant, index) => (
                <tr key={participant.id_reporte_participante}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      className="training-report-review-input"
                      type="text"
                      maxLength={255}
                      value={participant.nombre_completo}
                      onChange={(event) =>
                        updateParticipant(
                          participant.id_reporte_participante,
                          'nombre_completo',
                          event.target.value,
                        )
                      }
                      disabled={saving}
                    />
                  </td>
                  <td>
                    <input
                      className="training-report-review-input training-report-review-expediente"
                      type="text"
                      maxLength={50}
                      value={participant.matricula_alumno}
                      onChange={(event) =>
                        updateParticipant(
                          participant.id_reporte_participante,
                          'matricula_alumno',
                          event.target.value,
                        )
                      }
                      onBlur={(event) =>
                        updateParticipant(
                          participant.id_reporte_participante,
                          'matricula_alumno',
                          stripAboa(event.target.value),
                        )
                      }
                      disabled={saving}
                    />
                  </td>
                  <td>
                    <select
                      className="training-report-review-select"
                      value={participant.genero}
                      onChange={(event) =>
                        updateParticipant(
                          participant.id_reporte_participante,
                          'genero',
                          event.target.value,
                        )
                      }
                      disabled={saving}
                    >
                      <option value="">Pendiente</option>
                      <option value="H">H</option>
                      <option value="M">M</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="training-report-review-metrics">
        <div>
          <span>Participantes</span>
          <strong>{review.metrics.participants}</strong>
          <small>
            H: {review.metrics.men} · M: {review.metrics.women}
          </small>
        </div>
        <div>
          <span>Sesiones</span>
          <strong>{review.metrics.sessions}</strong>
          <small>{review.metrics.attendanceCells} registro(s) de asistencia</small>
        </div>
        <div>
          <span>Promedio grupal</span>
          <strong>
            {review.metrics.average === null ? 'Pendiente' : review.metrics.average}
          </strong>
          <small>Solo calificaciones finales numéricas</small>
        </div>
        <div>
          <span>Evidencias</span>
          <strong>{review.metrics.evidences}</strong>
          <small>Anexo 1</small>
        </div>
      </div>

      <div className="training-report-review-sections">
        {SECTIONS.map((section) => {
          const sectionStatus = issueStatusForSection(review, section);
          return (
            <article
              className={`training-report-review-section training-report-review-section-${sectionStatus.type}`}
              key={section}
            >
              <div>
                <strong>{section}</strong>
                <span>{sectionStatus.label}</span>
              </div>
              <small>
                {sectionStatus.count > 0
                  ? `${sectionStatus.count} hallazgo(s)`
                  : 'Sin hallazgos bloqueantes'}
              </small>
            </article>
          );
        })}
      </div>

      <div className="training-report-review-findings">
        <section>
          <div className="training-report-review-findings-heading">
            <div>
              <span>Errores bloqueantes</span>
              <strong>{review.blockers.length}</strong>
            </div>
            <small>Deben resolverse antes de generar el reporte definitivo.</small>
          </div>
          {review.blockers.length === 0 ? (
            <p className="training-report-review-empty training-report-review-empty-success">
              No hay errores bloqueantes.
            </p>
          ) : (
            <ul className="training-report-review-list training-report-review-list-error">
              {review.blockers.map((item) => (
                <li key={item.code}>
                  <strong>{item.section}: {item.message}</strong>
                  {item.detail && <span>{item.detail}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="training-report-review-findings-heading">
            <div>
              <span>Advertencias</span>
              <strong>{review.warnings.length}</strong>
            </div>
            <small>No bloquean el flujo, pero deben revisarse.</small>
          </div>
          {review.warnings.length === 0 ? (
            <p className="training-report-review-empty">No hay advertencias.</p>
          ) : (
            <ul className="training-report-review-list training-report-review-list-warning">
              {review.warnings.map((item) => (
                <li key={item.code}>
                  <strong>{item.section}: {item.message}</strong>
                  {item.detail && <span>{item.detail}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {review.ready && !loading && (
        <div className="admin-operation-status admin-operation-status-success training-report-review-next-note">
          El reporte está listo para generar una vista previa PDF.
        </div>
      )}

      <div className="training-report-editor-actions">
        <button
          className="admin-operation-button admin-operation-button-secondary"
          type="button"
          onClick={onPrevious}
          disabled={loading || saving}
        >
          Volver a evidencias
        </button>
        <button
          className="admin-operation-button"
          type="button"
          onClick={onNext}
          disabled={loading || saving || !review.ready}
        >
          Continuar a vista previa
        </button>
      </div>
    </>
  );
};
